import { useMemo } from 'react';
import {
    canSubmitRequest,
    validateRequest,
    type ValidationIssue,
} from '../../../utils/vacationRules';
import { partitionVacationIssues } from '../../../utils/vacationIssues';
import type { VacationBalance, VacationModality, VacationSegment } from '../../../models/vacation.types';

interface UseVacationFormValidationParams {
    modality: VacationModality | '';
    segments: VacationSegment[];
    sellDaysRequested: boolean;
    balance: VacationBalance | null;
    scheduleLimitDate?: string | null;
    hasRequiredDates: boolean;
}

interface UseVacationFormValidationResult {
    panelErrors: ValidationIssue[];
    panelWarnings: ValidationIssue[];
    fieldErrors: ValidationIssue[];
    fieldWarnings: ValidationIssue[];
    rulesAllowSubmit: boolean;
}

export function useVacationFormValidation({
    modality,
    segments,
    sellDaysRequested,
    balance,
    scheduleLimitDate,
    hasRequiredDates,
}: UseVacationFormValidationParams): UseVacationFormValidationResult {
    const businessValidation = useMemo(() => {
        if (!modality) return { valid: true, errors: [], warnings: [] };
        return validateRequest({ modality, segments, sellDaysRequested }, balance);
    }, [modality, segments, sellDaysRequested, balance]);

    const submissionCheck = useMemo(() => canSubmitRequest(balance, scheduleLimitDate), [balance, scheduleLimitDate]);

    const ruleIssues = [
        ...submissionCheck.errors,
        ...(hasRequiredDates ? businessValidation.errors : []),
    ];

    const advisories = [
        ...submissionCheck.warnings,
        ...(hasRequiredDates ? businessValidation.warnings : []),
    ];

    const { panelErrors, panelWarnings, fieldErrors, fieldWarnings } = partitionVacationIssues(
        ruleIssues,
        advisories
    );

    const rulesAllowSubmit = panelErrors.length === 0 && fieldErrors.length === 0;

    return {
        panelErrors,
        panelWarnings,
        fieldErrors,
        fieldWarnings,
        rulesAllowSubmit,
    };
}
